// Gate d'actions T1 : SEUL point de l'UI qui consulte la matrice de statuts et
// déclenche une transition. Situé hors de src/screens|components (INV-B : aucune
// logique de transition dans les écrans/composants). Les boutons d'une commande
// découlent EXACTEMENT de `transitionsAutoriseesDepuis(statut)` — donc à
// `en_finalisation`, « Annuler » n'apparaît pas parce que la matrice ne le
// retourne pas (INV-E émergent, jamais codé en dur).
import { updateCommandeStatut } from '@/db/transactions';
import { transitionsAutoriseesDepuis } from '@/lib/statuts';
import type { StatutCommande } from '@/db/schema';

// Libellés d'affichage par statut CIBLE — pure présentation (pas de logique de
// transition : l'ensemble des cibles vient de la matrice). « Commencer » =
// transition commandee→priorisee, libellé UI uniquement (LOT 7 §1, matrice W.9
// intouchée) — geste d'entrée en traitement, facultatif pour la visibilité.
const LIBELLE_CIBLE: Record<StatutCommande, string> = {
  commandee: 'Commandée',
  priorisee: 'Commencer',
  en_preparation: 'En préparation',
  enfournee: 'Enfourner',
  en_finalisation: 'En finalisation',
  prete: 'Pizza prête',
  cloturee: 'Clôturer',
  annulee: 'Annuler',
};

// Libellé du badge de statut COURANT (affichage file).
export const LIBELLE_STATUT: Record<StatutCommande, string> = {
  commandee: 'Commandée',
  priorisee: 'Priorisée',
  en_preparation: 'En préparation',
  enfournee: 'Enfournée',
  en_finalisation: 'En finalisation',
  prete: 'Prête',
  cloturee: 'Clôturée',
  annulee: 'Annulée',
};

// Libellé GROUPÉ (LOT 7 §2 — mapping d'affichage, matrice W.9 intouchée) :
// commandee → « À préparer » · priorisee…en_finalisation → « En cours » (badge
// détaillé conservé sur la carte) · prete → « Prête ».
export const LIBELLE_GROUPE: Record<StatutCommande, string> = {
  commandee: 'À préparer',
  priorisee: 'En cours',
  en_preparation: 'En cours',
  enfournee: 'En cours',
  en_finalisation: 'En cours',
  prete: 'Prête',
  cloturee: 'Clôturée',
  annulee: 'Annulée',
};

export interface ActionT1 {
  cible: StatutCommande;
  libelle: string;
  destructive: boolean;
}

export function actionsPour(statut: StatutCommande): ActionT1[] {
  return transitionsAutoriseesDepuis(statut).map((cible) => ({
    cible,
    libelle: LIBELLE_CIBLE[cible],
    destructive: cible === 'annulee',
  }));
}

// « Tout est prêt » (micro-lot 7f) — wrapper REPRENABLE et IDEMPOTENT au niveau
// UI-gate : depuis l'état RÉEL courant, poursuit les transitions EXISTANTES
// (cible non destructive issue de la MATRICE, jamais un chemin codé en dur)
// jusqu'à `prete`. Appels SÉQUENTIELS awaités = une intention utilisateur.
// NON-ATOMIQUE PAR CONSTRUCTION : chaque transition est persistée
// individuellement ; un échec intermédiaire CONSERVE l'état atteint (jamais de
// retour arrière) — la reprise repart du statut réellement persisté.
async function executerJusquaPrete(
  commandeId: number,
  depuis: StatutCommande,
): Promise<void> {
  let statut = depuis;
  while (statut !== 'prete') {
    const suivant = actionsPour(statut).find((a) => !a.destructive)?.cible;
    if (suivant === undefined) {
      // Statut terminal/hors chemin : rien à poursuivre (idempotence).
      return;
    }
    await updateCommandeStatut(commandeId, suivant);
    statut = suivant;
  }
}

export interface T1ActionGate {
  actionsPour: (statut: StatutCommande) => ActionT1[];
  executer: (commandeId: number, cible: StatutCommande) => Promise<void>;
  executerJusquaPrete: (
    commandeId: number,
    depuis: StatutCommande,
  ) => Promise<void>;
}

export function useT1ActionGate(): T1ActionGate {
  return {
    actionsPour,
    executer: (commandeId, cible) => updateCommandeStatut(commandeId, cible),
    executerJusquaPrete,
  };
}
